import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Button from "src/components/Button"
import Form from "src/components/Form"
import InlineNotice from "src/components/InlineNotice"
import Loader from "src/components/Loader"
import Modal from "src/components/Modal"
import SelectPaymentMethod, {
  ADD_NEW_PAYMENT_METHOD,
} from "src/components/SelectPaymentMethod/SelectPaymentMethod"

import {
  cancelContactPaymentMethodSetup,
  createContactPaymentMethod,
  initializeContactPaymentMethodSetup,
} from "src/api/Manage/Contacts/PaymentMethods"
import { getPaymentMethodOptions } from "src/api/Service/WorkOrders"

const AUTOPAY_ENABLED = "true"
const AUTOPAY_DISABLED = "false"
const FUTURE_ONLY = "false"
const APPLY_TO_EXISTING = "true"

const UpdateAutopayModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  workOrder,
  workOrderSequentialId,
}) => {
  const { marinaSlug } = useMarina()
  const contactId = workOrder.contact?.id

  const { handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: {
      autopayEnabled: workOrder.autopayEnabled
        ? AUTOPAY_ENABLED
        : AUTOPAY_DISABLED,
      stripePaymentMethodId:
        workOrder.autopayPaymentMethod?.stripePaymentMethodId ?? "",
      applyToExisting: FUTURE_ONLY,
    },
  })

  // Re-seed from the current work order each time the modal opens, so a save
  // (or a cancelled edit) is reflected on the next open without a page refresh.
  useEffect(() => {
    if (!isOpen) return

    reset({
      autopayEnabled: workOrder.autopayEnabled
        ? AUTOPAY_ENABLED
        : AUTOPAY_DISABLED,
      stripePaymentMethodId:
        workOrder.autopayPaymentMethod?.stripePaymentMethodId ?? "",
      applyToExisting: FUTURE_ONLY,
    })
  }, [isOpen, workOrder, reset])

  const [paymentMethods, setPaymentMethods] = useState([])

  const { data: fetchedPaymentMethods, isLoading } = useQuery({
    queryKey: ["workOrderPaymentMethods", marinaSlug, workOrderSequentialId],
    queryFn: () => getPaymentMethodOptions(marinaSlug, workOrderSequentialId),
    enabled: isOpen,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (fetchedPaymentMethods) setPaymentMethods(fetchedPaymentMethods)
  }, [fetchedPaymentMethods])

  const enabled = watch("autopayEnabled") === AUTOPAY_ENABLED
  const selectedPaymentMethodId = watch("stripePaymentMethodId")
  const applyToExisting = watch("applyToExisting")
  const hasUnsettledInvoices = workOrder.hasUnsettledInvoices
  const hasNoPaymentMethods = !isLoading && paymentMethods.length === 0
  const noMethodSelected =
    !selectedPaymentMethodId ||
    selectedPaymentMethodId === ADD_NEW_PAYMENT_METHOD
  const updateDisabled = isSubmitting || (enabled && noMethodSelected)

  const setAutopay = (event) =>
    setValue("autopayEnabled", event.target.value, { shouldDirty: true })

  const setApplyToExisting = (event) =>
    setValue("applyToExisting", event.target.value, { shouldDirty: true })

  const setPaymentMethod = (value) =>
    setValue("stripePaymentMethodId", value, { shouldDirty: true })

  const initializeSetup = () =>
    initializeContactPaymentMethodSetup({ contactId, marinaId: marinaSlug })

  const cancelSetup = (setupIntentId) =>
    cancelContactPaymentMethodSetup({
      contactId,
      marinaId: marinaSlug,
      setupIntentId,
    })

  const createPaymentMethod = (setupIntentId) =>
    createContactPaymentMethod({
      contactId,
      marinaId: marinaSlug,
      setupIntentId,
    })

  const handleValid = (values) => {
    const isEnabled = values.autopayEnabled === AUTOPAY_ENABLED
    onSubmit({
      autopayEnabled: isEnabled,
      stripePaymentMethodId: isEnabled ? values.stripePaymentMethodId : null,
      applyToExisting: Boolean(
        hasUnsettledInvoices && values.applyToExisting === APPLY_TO_EXISTING
      ),
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="mediumFixed">
      <Modal.Header
        title="Update Autopay"
        subtext={`${workOrder.displaySequentialId} · ${workOrder.contact?.name}`}
      />
      <Modal.Body>
        {isLoading ? (
          <Loader name="payment methods" />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <Form.Label htmlFor="autopay">AutoPay</Form.Label>
              <Form.Select
                id="autopay"
                value={enabled ? AUTOPAY_ENABLED : AUTOPAY_DISABLED}
                onChange={setAutopay}
              >
                <option value={AUTOPAY_ENABLED}>Enabled</option>
                <option value={AUTOPAY_DISABLED}>
                  Disabled (Settle Manually)
                </option>
              </Form.Select>
              <p className="text-sm text-gray-500">
                {enabled
                  ? "Invoices on this work order auto-charge on their due date."
                  : "Invoices on this work order require manual collection."}
              </p>
            </div>

            {enabled && (
              <>
                {hasNoPaymentMethods && (
                  <InlineNotice variant="error" icon="fa-exclamation-circle">
                    This user has no payment methods. Add one below to enable
                    autopay.
                  </InlineNotice>
                )}
                <SelectPaymentMethod
                  acceptedPaymentMethods={["card"]}
                  paymentMethods={paymentMethods}
                  setPaymentMethods={setPaymentMethods}
                  paymentMethod={selectedPaymentMethodId}
                  setPaymentMethod={setPaymentMethod}
                  suppressHeading
                  initializeSetup={initializeSetup}
                  cancelSetup={cancelSetup}
                  createPaymentMethod={createPaymentMethod}
                />
              </>
            )}

            {hasUnsettledInvoices ? (
              <>
                <div className="flex flex-col gap-2 rounded-lg border p-4">
                  <span>Would you like to apply these changes to:</span>
                  <div className="space-y-3">
                    <Form.RadioButton
                      name="applyToExisting"
                      value={FUTURE_ONLY}
                      checked={applyToExisting === FUTURE_ONLY}
                      onChange={setApplyToExisting}
                      label="Only future invoices from this work order"
                    />
                    <Form.RadioButton
                      name="applyToExisting"
                      value={APPLY_TO_EXISTING}
                      checked={applyToExisting === APPLY_TO_EXISTING}
                      onChange={setApplyToExisting}
                      label="All future and existing unsettled invoices from this work order"
                    />
                  </div>
                </div>
                {applyToExisting === APPLY_TO_EXISTING && (
                  <InlineNotice variant="warning" icon="fa-exclamation-circle">
                    Applying to existing invoices resets any per-invoice AutoPay
                    overrides on this work order&rsquo;s unsettled invoices.
                  </InlineNotice>
                )}
              </>
            ) : (
              <InlineNotice variant="info">
                Changes take effect on the next invoice issued from this work
                order.
              </InlineNotice>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="flex w-full items-center justify-between gap-2">
          <a
            href={workOrder.contactPaymentMethodsPath}
            className="font-semibold text-blue-600"
          >
            Edit Contact&rsquo;s Payment Methods
          </a>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              icon="fa-check"
              onClick={handleSubmit(handleValid)}
              isLoading={isSubmitting}
              disabled={updateDisabled}
            >
              Update
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

UpdateAutopayModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  workOrder: PropTypes.shape({
    displaySequentialId: PropTypes.string,
    contactPaymentMethodsPath: PropTypes.string,
    contact: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    autopayEnabled: PropTypes.bool,
    autopayPaymentMethod: PropTypes.shape({
      stripePaymentMethodId: PropTypes.string,
    }),
    hasUnsettledInvoices: PropTypes.bool,
  }).isRequired,
  workOrderSequentialId: PropTypes.string.isRequired,
}

export default UpdateAutopayModal
